import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import heroImage from "@/assets/hero-landscape.jpg";

// Use public path for mobile image to enable preloading in HTML
const heroMobileImage = "/hero-mobile.png";

const Hero = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [isImageActive, setIsImageActive] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleImageTap = () => {
    if (isMobile) {
      setIsImageActive(!isImageActive);
    }
  };

  return (
    <section className="relative lg:min-h-[180vh]">
      {/* Desktop Background Image - scrolls normally */}
      <div className="hidden lg:block group absolute inset-x-0 top-0">
        <img
          src={heroImage}
          alt="Modern poolside landscape with pergola and tropical plants"
          className="w-full h-auto animate-scale-in grayscale group-hover:grayscale-0 transition-all duration-500"
          width={2000}
          height={1344}
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/20 to-background pointer-events-none" />
      </div>

      {/* Mobile Hero Layout - Image with text below */}
      <div className="lg:hidden flex flex-col">
        {/* Mobile Image - full width, natural height, NO cropping */}
        <div className="pt-16 sticky top-0 z-0 relative" onClick={handleImageTap}>
          <img
            src={heroMobileImage}
            alt="Modern poolside landscape with pergola and tropical plants"
            className={`w-full h-auto transition-all duration-500 ${
              isImageActive ? 'grayscale-0' : 'grayscale'
            }`}
            width={1920}
            height={1290}
            fetchPriority="high"
            style={{ aspectRatio: '1920/1290' }}
          />
          {/* Gradient overlay for mobile - same as desktop */}
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/20 to-background pointer-events-none" />
        </div>

        {/* Mobile Text Content - scrolls over image */}
        <div className="relative z-10 px-6 py-8 -mt-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Japanese Character */}
            <div className="opacity-0 animate-fade-up mb-4">
              <span className="text-5xl font-serif text-foreground/20">道</span>
            </div>

            {/* Tagline */}
            <p className="opacity-0 animate-fade-up animation-delay-100 text-label text-muted-foreground mb-3">
              {t('hero.kata')}
            </p>

            {/* Main Headline */}
            <h1 className="opacity-0 animate-fade-up animation-delay-200 text-display-lg font-serif text-foreground mb-3">
              {t('hero.headline')}
            </h1>

            {/* Subheadline */}
            <p className="opacity-0 animate-fade-up animation-delay-300 text-body text-muted-foreground max-w-2xl mx-auto mb-6 text-base">
              {t('hero.subtitle')}
            </p>

            {/* CTAs - Only quiz button on mobile/tablet */}
            <div className="opacity-0 animate-fade-up animation-delay-400 flex flex-col items-center">
              <Button variant="hero" size="xl" asChild>
                <Link to="/quiz">{t('hero.cta')}</Link>
              </Button>
              <p className="text-sm text-muted-foreground mt-3">{t('hero.ctaMicro')}</p>
            </div>
          </div>
        </div>
      </div>


      {/* Desktop Content - sticky on desktop */}
      <div className="relative hidden lg:flex lg:sticky lg:top-0 lg:h-screen items-center justify-center pointer-events-none py-20 lg:py-0">
        <div className="container mx-auto px-6 lg:px-12 pt-16 lg:pt-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Japanese Character */}
            <div className="opacity-0 animate-fade-up mb-4 lg:mb-8">
              <span className="text-5xl lg:text-8xl font-serif text-foreground/20">道</span>
            </div>

            {/* Tagline */}
            <p className="opacity-0 animate-fade-up animation-delay-100 text-label text-muted-foreground mb-3 lg:mb-6">
              {t('hero.kata')}
            </p>

            {/* Main Headline */}
            <h1 className="opacity-0 animate-fade-up animation-delay-200 text-display-lg lg:text-display-xl font-serif text-foreground mb-3 lg:mb-6">
              {t('hero.headline')}
            </h1>

            {/* Subheadline */}
            <p className="opacity-0 animate-fade-up animation-delay-300 text-body text-muted-foreground max-w-2xl mx-auto mb-6 lg:mb-10 text-base lg:text-lg">
              {t('hero.subtitle')}
            </p>

            {/* CTAs */}
            <div className="opacity-0 animate-fade-up animation-delay-400 flex flex-col items-center pointer-events-auto">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="hero" size="xl" asChild>
                  <Link to="/quiz">{t('hero.cta')}</Link>
                </Button>
                <Button variant="hero-outline" size="xl" asChild>
                  <a href="#portfolio">{t('portfolio.viewAll')}</a>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-3">{t('hero.ctaMicro')}</p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 animate-fade-in animation-delay-700">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-foreground/30 to-foreground/10" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
